import React from "react";
import styled from "styled-components";

/** 캠페인 목록을 조회할 광고주를 선택하는 공통 목록 테이블. */
export default function AdvertiserListTable({
  advertisers = [],
  selectedAdvertiserId,
  onAdvertiserSelect,
  countKey = "campaignCount",
  height,
  maxVisibleRows = 4,
  emptyText = "조회된 광고주가 없습니다."
}) {
  return (
      <TableCard $height={height}>
        <TableHeader>
          <span>광고주</span>
          <span>캠페인</span>
        </TableHeader>

        <TableBody $maxVisibleRows={maxVisibleRows} $height={height}>
          {advertisers.length === 0 ? (
              <EmptyState>{emptyText}</EmptyState>
          ) : advertisers.map((advertiser, index) => {
            const selected = String(selectedAdvertiserId) === String(advertiser?.userId);
            const campaignCount = Number(
                advertiser?.[countKey] ?? advertiser?.campaignCount ?? advertiser?.creativeCount ?? 0
            );

            return (
                <AdvertiserRow
                    key={advertiser?.userId}
                    type="button"
                    $selected={selected}
                    $even={index % 2 === 1}
                    aria-pressed={selected}
                    onClick={() => onAdvertiserSelect?.(advertiser)}
                >
                  <AdvertiserNameCell>
                    <AdvertiserTextGroup>
                      <AdvertiserName>{advertiser?.adverName ?? "-"}</AdvertiserName>
                      <AdvertiserId>{advertiser?.username ?? "-"}</AdvertiserId>
                    </AdvertiserTextGroup>
                    {/*{selected && <SelectedBadge>선택됨</SelectedBadge>}*/}
                  </AdvertiserNameCell>

                  <CampaignCountCell>
                    <CampaignCountBadge>{campaignCount}</CampaignCountBadge>
                  </CampaignCountCell>
                </AdvertiserRow>
            );
          })}
        </TableBody>
      </TableCard>
  );
}

const TableCard = styled.div`
  display: flex;
  width: 100%;
  height: ${({$height}) => $height ? (typeof $height === "number" ? `${$height}px` : $height) : "auto"};
  overflow: hidden;
  flex-direction: column;
  border: 1px solid #e7d8cc;
  border-radius: 12px;
  background-color: #fff;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 62px;
  min-height: 48px;
  align-items: center;
  padding: 0 14px;
  border-bottom: 2px solid #f0c5a3;
  background: linear-gradient(180deg, #fffaf6 0%, #fff4e9 100%);
  color: #4b5158;
  font-size: var(--font-size-label);
  font-weight: 800;
  letter-spacing: -0.01em;

  span:last-child { text-align: center; }
`;

const TableBody = styled.div`
  min-height: 0;
  max-height: ${({$maxVisibleRows, $height}) => $height ? "none" : `calc(58px * ${$maxVisibleRows})`};
  flex: ${({$height}) => $height ? 1 : "none"};
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c7c7c7 transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background-color: transparent; }
  &::-webkit-scrollbar-thumb { border-radius: 999px; background-color: #c7c7c7; }
`;

const AdvertiserRow = styled.button`
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) 62px;
  min-height: 58px;
  align-items: center;
  padding: 0 14px;
  border: 0;
  border-bottom: 1px solid #edf0f2;
  background: ${({$selected, $even}) =>
      $selected
          ? "linear-gradient(90deg, #fff0e2 0%, #fff8f1 100%)"
          : $even
              ? "#fcfcfd"
              : "#fff"};
  color: ${({$selected}) => $selected ? "#343a40" : "#4c545c"};
  text-align: left;
  cursor: pointer;
  box-shadow: ${({$selected}) => $selected
      ? "inset 4px 0 0 #f5811f, inset 0 1px 0 rgba(245,129,31,.18), inset 0 -1px 0 rgba(245,129,31,.18)"
      : "none"};
  transition: background-color 140ms ease, box-shadow 140ms ease;

  &:last-child { border-bottom: 0; }
  &:hover {
    z-index: 1;
    background: ${({$selected}) => $selected
        ? "linear-gradient(90deg, #ffe9d4 0%, #fff4e9 100%)"
        : "#fff8f1"};
    box-shadow: ${({$selected}) => $selected
        ? "inset 4px 0 0 #f5811f, inset 0 1px 0 rgba(245,129,31,.18), inset 0 -1px 0 rgba(245,129,31,.18)"
        : "inset 3px 0 0 #f5811f"};
  }
  &:focus-visible { position: relative; z-index: 1; outline: 1px solid #f5811f; outline-offset: -1px; }
`;

const AdvertiserNameCell = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;
const AdvertiserTextGroup = styled.span`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 3px;
`;
const AdvertiserName = styled.span`
  overflow: hidden;
  color: #3e454b;
  font-size: var(--font-size-body);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const AdvertiserId = styled.span`
  overflow: hidden;
  color: #999;
  font-size: var(--font-size-micro);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const SelectedBadge = styled.span`
  display: inline-flex;
  min-width: 42px;
  height: 21px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-micro);
  font-weight: 700;
`;
const CampaignCountCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const CampaignCountBadge = styled.span`
  display: inline-flex;
  min-width: 38px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border-radius: 12px;
  background-color: transparent;
  color: #4c545c;
  font-size: var(--font-size-body);
  font-weight: 600;
`;
const EmptyState = styled.div`
  padding: 42px 20px;
  color: #999;
  font-size: var(--font-size-label);
  text-align: center;
`;
